/**
 * Effect implementation for reactive computations
 */

import {   currentEffect,  currentContext,  CLEAN,  STALE,  setCurrentEffect,  incrementEffectId,  EffectFunction} from './constants';
import { debugEffect } from './debug';

// Type for effect cleanup function
export type Dispose = () => void;
export type EffectCallback = () => void | (() => void);

/**
 * Creates an effect that tracks reactive dependencies
 * 
 * @param fn The function to run as an effect
 * @returns A dispose function that can be called to clean up the effect
 */
export function createEffect(fn: EffectCallback): Dispose {
  // Create the effect function
  const effect: EffectFunction = () => {
    // Skip if already disposed
    if (effect.disposed) {
      debugEffect(effect.id, 'Skipping execution - disposed');
      return;
    }

    debugEffect(effect.id, 'Running effect');

    // Run cleanup functions if any
    if (effect.cleanupFns && effect.cleanupFns.length > 0) {
      debugEffect(effect.id, `Running ${effect.cleanupFns.length} cleanup functions`);
      for (let i = 0; i < effect.cleanupFns.length; i++) {
        effect.cleanupFns[i]();
      }
      effect.cleanupFns = [];
    }

    // Clear previous dependencies
    if (effect.sources && effect.sources.length > 0) {
      debugEffect(effect.id, `Clearing ${effect.sources.length} sources`);
      // Remove this effect from all previous sources
      for (let i = 0; i < effect.sources.length; i++) {
        const sourceNode = effect.sources[i];
        sourceNode.subscribers.delete(effect);
      }
      effect.sources.length = 0;  // Clear array
      effect.sourceSlots.length = 0;  // Clear slots
    }

    // Prevent recursive effect execution
    if (effect.running) {
      debugEffect(effect.id, 'Preventing recursive execution');
      return;
    }
    effect.running = true;
    
    // Set as current effect during execution to track dependencies
    const prevEffect = currentEffect;
    setCurrentEffect(effect);
    
    debugEffect(effect.id, 'Set as currentEffect:', currentEffect);
    
    try {
      // Run the effect function - this will track dependencies via signal getters
      const result = fn();
      
      // Handle cleanup function returned from effect
      if (typeof result === 'function') {
        debugEffect(effect.id, 'Registered cleanup function');
        effect.cleanupFns.push(result);
      }
    } catch (error) {
      console.error(`[EFFECT #${effect.id}] Error in effect:`, error);
    } finally {
      // Restore previous effect
      setCurrentEffect(prevEffect);
      effect.running = false;
      debugEffect(effect.id, 'Restored currentEffect to:', currentEffect);
    }
  };

  // Initialize effect properties
  effect.id = incrementEffectId();
  effect.disposed = false;
  effect.running = false;
  effect.state = CLEAN;
  effect.cleanupFns = [];
  effect.sources = [];           // Array instead of Set
  effect.sourceSlots = [];       // Track positions for O(1) removal
  effect.updatedAt = null;       // Track when last updated
  effect.parentContext = currentContext;
  
  debugEffect(effect.id, 'Created new effect');
  
  // Register with parent context if available
  if (currentContext && currentContext.effects) {
    currentContext.effects.add(effect);
    debugEffect(effect.id, 'Registered with parent context');
  }

  // Dispose function to clean up the effect
  effect.dispose = () => {
    if (effect.disposed) {
      debugEffect(effect.id, 'Already disposed, skipping');
      return;
    }
    
    debugEffect(effect.id, 'Disposing effect');
    effect.disposed = true;

    // Run cleanup functions
    if (effect.cleanupFns.length > 0) {
      debugEffect(effect.id, `Running ${effect.cleanupFns.length} cleanup functions before disposal`);
      for (let i = 0; i < effect.cleanupFns.length; i++) {
        effect.cleanupFns[i]();
      }
      effect.cleanupFns = [];
    }

    // Remove from all sources
    if (effect.sources && effect.sources.length > 0) {
      debugEffect(effect.id, `Removing from ${effect.sources.length} sources`);
      for (let i = 0; i < effect.sources.length; i++) {
        const sourceNode = effect.sources[i];
        sourceNode.subscribers.delete(effect);
      }
      effect.sources.length = 0;  // Clear array
      effect.sourceSlots.length = 0;  // Clear slots
    }

    // Remove from parent context
    if (effect.parentContext && effect.parentContext.effects) {
      effect.parentContext.effects.delete(effect);
      debugEffect(effect.id, 'Removed from parent context');
    }
  };

  // Initial execution to establish dependencies
  debugEffect(effect.id, 'Initial execution');
  effect();
  
  // Return the dispose function
  return effect.dispose;
} 